/**
 * Shipment — `/sales/fulfilments/$fulfilmentId`.
 *
 * One shipment and the moves it can still make.
 *
 * Shipping is the only move with an effect beyond this screen, so it is the
 * only one that asks for anything: the carrier and the tracking reference the
 * customer will be given. What it does is stated plainly, because it is not
 * reversible here — the reservations are consumed, the stock positions fall by
 * the weighted-average cost of what left, and the ledger is handed the cost to
 * post. After that the correction is a return.
 *
 * A refusal is shown as the rule that stopped it. "Not enough stock" and "more
 * than the customer ordered" are answers an operator can act on; "something
 * went wrong" is not.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Receipt, Truck } from "lucide-react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@erp/ui";

import { ApiError } from "@/lib/api/errors";
import { MoneyDisplay } from "@/components/money/money-display";
import { newIdempotencyKey } from "@/features/shared/ops-api";
import { formatDateTime } from "@/lib/format";

import {
  FULFILMENT_TRANSITIONS,
  transitionFulfilment,
  type FulfilmentStatus,
} from "../fulfilments-api";
import { salesKeys, useFulfilment } from "../queries";

export interface FulfilmentDetailPageProps {
  fulfilmentId: string;
  canExecute?: boolean;
  baseCurrency?: string;
  onOpenOrder: (salesOrderId: string) => void;
  /** `finance.customer-invoice.create` — the hand-off to finance. */
  onInvoice?: (fulfilmentId: string) => void;
}

export function FulfilmentDetailPage({
  fulfilmentId,
  canExecute = false,
  baseCurrency = "SAR",
  onOpenOrder,
  onInvoice,
}: FulfilmentDetailPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const detail = useFulfilment(fulfilmentId);
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fulfilment = detail.data?.fulfilment;
  const status: FulfilmentStatus = fulfilment?.status ?? "allocated";
  const nextStates = FULFILMENT_TRANSITIONS[status];

  const stamp = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const move = (to: FulfilmentStatus) => {
    setBusy(true);
    setError(null);
    void transitionFulfilment(
      fulfilmentId,
      to,
      to === "shipped" ? { carrier, tracking_reference: tracking } : {},
      newIdempotencyKey(),
    )
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: salesKeys.fulfilments.all });
      })
      .catch((caught: unknown) => {
        const code = caught instanceof ApiError ? (caught.code ?? "") : "";
        setError(
          code === ""
            ? t("sales.fulfilmentDetail.moveError", "That move was refused.")
            : t(`sales.fulfilmentRefusal.${code}`, code),
        );
      })
      .finally(() => setBusy(false));
  };

  if (detail.isLoading || fulfilment === undefined) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight" dir="ltr">
          {fulfilment.fulfilment_number}
        </h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant={status === "shipped" ? "success" : "secondary"}>
            {t(`sales.fulfilmentStatus.${status}`, status)}
          </Badge>
          {fulfilment.shipped_at === null ? null : (
            <span>
              {t("sales.fulfilmentDetail.shippedAt", "Left {{time}}", {
                time: stamp(fulfilment.shipped_at),
              })}
            </span>
          )}
          <button
            type="button"
            className="underline underline-offset-4"
            onClick={() => onOpenOrder(fulfilment.sales_order_id)}
          >
            {t("sales.fulfilmentDetail.openOrder", "Open the order")}
          </button>
        </p>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("sales.fulfilmentDetail.lines", "What is on it")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="border-b text-xs uppercase text-muted-foreground">
                  <th scope="col" className="p-2 text-start">
                    {t("sales.fulfilmentDetail.variant", "Item")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("sales.fulfilmentDetail.qty", "Quantity")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("sales.fulfilmentDetail.issued", "Cost when it left")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(detail.data?.lines ?? []).map((line) => (
                  <tr key={line.id} className="border-b last:border-0">
                    <td className="p-2 font-mono text-xs" dir="ltr">
                      {line.variant_id.slice(0, 8)}
                    </td>
                    <td className="p-2 text-end tabular-nums" dir="ltr">
                      {line.qty}
                    </td>
                    <td className="p-2 text-end">
                      {line.issued_value === null ? (
                        <span className="text-muted-foreground">
                          {t("sales.fulfilmentDetail.notYet", "Not yet")}
                        </span>
                      ) : (
                        <MoneyDisplay
                          value={{ amount: line.issued_value, currency: baseCurrency }}
                          display="none"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {status === "shipped" && onInvoice !== undefined ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("sales.fulfilmentDetail.billing", "Billing")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "sales.fulfilmentDetail.invoiceHint",
                "The invoice cites this shipment, so the same goods cannot be billed twice unnoticed.",
              )}
            </p>
            <Button type="button" variant="outline" onClick={() => onInvoice(fulfilmentId)}>
              <Receipt className="size-4" aria-hidden="true" />
              {t("sales.fulfilmentDetail.invoice", "Invoice this shipment")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {!canExecute || nextStates.length === 0 ? (
        status === "shipped" ? (
          <p className="text-sm text-muted-foreground">
            {t(
              "sales.fulfilmentDetail.gone",
              "These goods have left. The correction for a shipment is a return, not a cancellation.",
            )}
          </p>
        ) : null
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("sales.fulfilmentDetail.next", "What happens next")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {nextStates.includes("shipped") ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "sales.fulfilmentDetail.shipHint",
                    "Shipping consumes the reservations, reduces the stock by what the goods cost, and hands the ledger that cost to post. It cannot be undone here.",
                  )}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="fulfilment-carrier">
                      {t("sales.fulfilmentDetail.carrier", "Carrier")}
                    </Label>
                    <Input
                      id="fulfilment-carrier"
                      value={carrier}
                      onChange={(event) => setCarrier(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fulfilment-tracking">
                      {t("sales.fulfilmentDetail.tracking", "Tracking reference")}
                    </Label>
                    <Input
                      id="fulfilment-tracking"
                      dir="ltr"
                      className="font-mono"
                      value={tracking}
                      onChange={(event) => setTracking(event.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {nextStates.map((next) => (
                <Button
                  key={next}
                  type="button"
                  variant={next === "shipped" ? "default" : "outline"}
                  disabled={busy}
                  onClick={() => move(next)}
                >
                  {next === "shipped" ? <Truck className="size-4" aria-hidden="true" /> : null}
                  {t(`sales.fulfilmentMove.${next}`, next)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
